import type { IntegrationJob, IntegrationRun, IntegrationStatus } from "../../shared/types";
import { getScheduleOccurrencesForDate, scheduleSlotKey } from "./cronSchedule";

interface BuildProjectedRunsOptions {
  jobs: IntegrationJob[];
  runs: IntegrationRun[];
  dateStart?: string;
  dateEnd?: string;
  now?: Date;
}

interface Slot {
  key: string;
  expectedStartTime: string;
  startMs: number;
}

const MAX_PROJECTION_DAYS = 31;

function utcDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function dateFromInput(value: string | undefined, fallback: Date): Date {
  if (!value) {
    return new Date(Date.UTC(fallback.getUTCFullYear(), fallback.getUTCMonth(), fallback.getUTCDate()));
  }

  return new Date(`${value}T00:00:00.000Z`);
}

function dateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const startMs = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  const normalizedEnd = Math.max(startMs, endMs);

  for (let current = startMs; current <= normalizedEnd && dates.length < MAX_PROJECTION_DAYS; current += 24 * 60 * 60_000) {
    dates.push(new Date(current));
  }

  return dates;
}

function projectedStatus(expectedStartTime: string, job: IntegrationJob, now: Date): IntegrationStatus {
  const expectedMs = new Date(expectedStartTime).getTime();
  const graceCutoff = expectedMs + job.graceMinutes * 60_000;
  return now.getTime() > graceCutoff ? "MISSING" : "SCHEDULED";
}

function createProjectedRun(job: IntegrationJob, expectedStartTime: string, now: Date): IntegrationRun {
  const status = projectedStatus(expectedStartTime, job, now);

  return {
    id: `${job.id}-${new Date(expectedStartTime).toISOString().replace(/\D/g, "").slice(0, 14)}-projected`,
    jobId: job.id,
    jobName: job.name,
    sourceType: job.sourceType,
    sourceName: job.sourceName,
    workspaceName: job.workspaceName,
    pipelineName: job.pipelineName || job.name,
    triggerName: job.triggerName,
    expectedStartTime,
    status,
    severity: job.severity,
    lastError: status === "MISSING" ? "No run detected after the configured grace period." : undefined,
    owner: job.owner,
    invokedBy: "CronExpression",
    lastCheckedAt: now.toISOString()
  };
}

function isRunInsideWindow(run: IntegrationRun, start: Date, end: Date): boolean {
  const startMs = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endMs = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 23, 59, 59, 999);
  const expectedMs = new Date(run.expectedStartTime).getTime();
  return expectedMs >= startMs && expectedMs <= endMs;
}

function findMatchingSlot(run: IntegrationRun, job: IntegrationJob, slots: Slot[]): Slot | undefined {
  const expectedMs = new Date(run.expectedStartTime).getTime();
  const exactExpected = slots.find((slot) => Math.abs(slot.startMs - expectedMs) <= 60_000);
  if (exactExpected) {
    return exactExpected;
  }

  const referenceMs = new Date(run.actualStartTime || run.endTime || run.expectedStartTime).getTime();
  if (Number.isNaN(referenceMs)) {
    return undefined;
  }

  const toleranceMinutes = Math.max(job.graceMinutes, job.expectedDurationMinutes, 120);
  const toleranceMs = toleranceMinutes * 60_000;
  return slots
    .filter((slot) => Math.abs(slot.startMs - referenceMs) <= toleranceMs)
    .sort((left, right) => Math.abs(left.startMs - referenceMs) - Math.abs(right.startMs - referenceMs))[0];
}

export function buildProjectedRuns(options: BuildProjectedRunsOptions): IntegrationRun[] {
  const now = options.now || new Date();
  const start = dateFromInput(options.dateStart, now);
  const end = dateFromInput(options.dateEnd || options.dateStart, start);
  const jobsById = new Map(options.jobs.map((job) => [job.id, job]));
  const combined = new Map<string, IntegrationRun>();
  const slotsByJobId = new Map<string, Slot[]>();
  const actualSlotCounts = new Map<string, number>();

  for (const job of options.jobs) {
    if (!job.isActive) {
      continue;
    }

    const slots: Slot[] = [];
    for (const date of dateRange(start, end)) {
      for (const expectedStartTime of getScheduleOccurrencesForDate(job, date)) {
        const key = scheduleSlotKey(job.id, expectedStartTime);
        const slot = {
          key,
          expectedStartTime,
          startMs: new Date(expectedStartTime).getTime()
        };
        slots.push(slot);
        combined.set(key, createProjectedRun(job, expectedStartTime, now));
      }
    }

    slotsByJobId.set(job.id, slots);
  }

  for (const run of options.runs) {
    const job = jobsById.get(run.jobId);
    const slots = job ? slotsByJobId.get(job.id) || [] : [];
    const matchingSlot = job ? findMatchingSlot(run, job, slots) : undefined;

    if (matchingSlot) {
      const count = actualSlotCounts.get(matchingSlot.key) || 0;
      actualSlotCounts.set(matchingSlot.key, count + 1);
      const normalizedRun = {
        ...run,
        expectedStartTime: matchingSlot.expectedStartTime
      };
      combined.set(count === 0 ? matchingSlot.key : `actual|${run.id}`, normalizedRun);
      continue;
    }

    if (isRunInsideWindow(run, start, end)) {
      combined.set(`actual|${run.id}`, run);
    }
  }

  return [...combined.values()].sort((left, right) => {
    const expectedDelta = new Date(right.expectedStartTime).getTime() - new Date(left.expectedStartTime).getTime();
    if (expectedDelta !== 0) {
      return expectedDelta;
    }

    return (left.jobName || left.jobId).localeCompare(right.jobName || right.jobId);
  });
}

export function todayUtcString(): string {
  return utcDateString(new Date());
}
